/* eslint-disable @typescript-eslint/ban-ts-ignore */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useIntl } from 'react-intl';
import { CircularProgress } from '@material-ui/core/';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components/macro';
import { BackTo, NoData, showToast, Typography } from '@deeptrue-frontend/ui';
import { OrganizationType } from 'store/modules/auth/auth';
import { Dashboard } from 'components/Layout/';
import { useFormatMessage } from 'components/IntlProvider';
import ProjectMenu from '../../Common/ProjectMenu';
import { WithProjectProps } from '../../withProject';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { ReportsType } from './types';
import { ProjectStatus } from 'consts/statuses';

import ReportTabs from './ReportTabs';
import { translateGroupName } from './utils';
import Results from './Results/Results';
import Specification from './Specification/Specification';
import Files from './Files/Files';
import api from 'api';
//import { insights } from './Mocks/insights'; /*LEFT FOR TESTING*/
import Insights from './Insights/Insights';
import { ReportSharing } from './ReportSharing/RepoortSharing';

const { connect, selectors } = StoreProvider;

interface ProjectProps
  extends RouteComponentProps<{ id: string; codename: string }> {
  product: WithProjectProps['product'];
  project: WithProjectProps['project'];
  organization: OrganizationType;
  isAdmin: boolean;
  isBexioUser: boolean;
}

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1.56rem 2rem;
  width: calc(100% + 2rem);
  background-color: #ffffff;
  border-bottom: 1px solid #e6e6e5;
`;

const WrapperNoData = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10rem;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 5rem;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const Container = styled.div`
  margin-left: -2rem;
  display: flex;
  width: calc(100% + 2rem);
  flex-direction: column;
`;

const ProjectMenuWrapper = styled.div`
  width: calc(100% - 2rem);
  .tab-bar {
    margin-left: -2rem;
    padding-left: 2rem;
    margin-right: -2rem;
    padding-right: 2rem;
  }
`;

const Title = styled(Typography)`
  font-size: 1.25rem;
  font-weight: 500;
  color: #253858;
`;

export enum Tabs {
  'reportRecommendations' = -1,
  'results' = 0,
  'specification',
  'files',
}

const DefaultReport = ({
  location,
  match: { params },
  project,
  isAdmin,
  organization,
}: any) => {
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTab, changeTab] = useState(Tabs.results);
  const [report, setReport] = useState<ReportsType | null>(null);
  const [selectedSection, setSection] = useState<string | undefined>();
  const history = useHistory();
  const choosenTab = Tabs[selectedTab];
  const { locale } = useIntl();
  // @ts-ignore
  const dataToDisplay = report ? report[choosenTab] : [];

  const t = useFormatMessage();

  useEffect(() => {
    if (project.id !== -1) {
      api.resources
        .getDefaultReport({
          organizationCodename: organization,
          projectUuid: project.uuid,
        })
        .then(({ data }) => {
          const isReportReady = data && data.results && data.results.length > 0;

          if (isReportReady) {
            setReport({
              ...data,
            });
          }
          setIsLoading(false);
        })
        .catch(() => {
          showToast({
            type: 'error',
            title: t(
              'errors.toast.general',
              'Something went wrong, please contact administrator'
            ),
          });
          setIsLoading(false);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  useEffect(() => {
    if (selectedTab === Tabs.files) {
      return;
    }
    const section = dataToDisplay[0];

    if (section) {
      setSection(
        translateGroupName(section.group_name || section.title, locale)
      );
    }
  }, [dataToDisplay, locale, selectedTab]);

  const overviewUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/`
    : `/`;

  const noReport =
    project.status.value === ProjectStatus.COLLECTING_DATA ? (
      <NoData
        title={t(
          'project.statistic.bexio.nodata.report.title',
          'Waiting for responses'
        )}
        description={t(
          'project.statistic.nodata.report.description',
          'We will now automatically send the survey to your audience. As soon as the first responses are available, the report will be generated.'
        )}
      />
    ) : (
      <NoData
        title={t(
          'project.statistic.nodata.finished.report.title',
          'No data collected'
        )}
        description={t(
          'project.statistic.nodata.finished.report.description',
          'The project was completed before any data had been collected.'
        )}
        completed
      />
    );

  const isReportAvailable = !!report;
  return (
    <Dashboard
      title={project.name || t('page.project', 'Project details')}
      location={location}
      isFullWidth
      combinedTitle={project.combinedTitle}
    >
      <BackTo onClick={() => history.push(overviewUrl)}>
        {t('project.backTo.overview', 'Back to overview')}
      </BackTo>
      <ProjectMenuWrapper>
        <ProjectMenu
          id={params.id}
          projectStatus={project.status}
          isProjectLoaded={project.id !== -1}
        />
      </ProjectMenuWrapper>

      {isLoading ? (
        <LoaderWrapper>
          <CircularProgress size={80} color="primary" />
        </LoaderWrapper>
      ) : isReportAvailable ? (
        <Container>
          <Wrapper>
            <Title>Report</Title>
            <ReportSharing
              surveyId={project.surveyId}
              organization={organization}
            />
          </Wrapper>

          <ReportTabs
            changeTab={changeTab}
            selectedTab={selectedTab}
            isAdmin={isAdmin}
          />

          {selectedTab === Tabs.reportRecommendations && (
            <Insights
              dataToDisplay={dataToDisplay}
              setSection={setSection}
              selectedSection={selectedSection}
              changeTab={changeTab}
            />
          )}

          {selectedTab === Tabs.results && (
            <Results
              setSection={setSection}
              selectedSection={selectedSection}
              dataToDisplay={dataToDisplay}
            />
          )}
          {selectedTab === Tabs.specification && (
            <Specification data={dataToDisplay} />
          )}
          {selectedTab === Tabs.files && (
            <Files
              projectUUID={project.uuid}
              organization={params.organization}
            />
          )}
        </Container>
      ) : (
        <WrapperNoData>{noReport}</WrapperNoData>
      )}
    </Dashboard>
  );
};

export default connect((state) => ({
  isAdmin: selectors.authSelectors.isDeepTrueAdmin(state),
  codename: selectors.organizationsSelectors.organizationById,
}))(DefaultReport);
